import React, {useState, useEffect, useRef, useCallback} from "react";

const AnswerQuestionSurvey = ({ 
  questionId,
  formatId,
  title,
  image,
  obligatory = false,
  options = [],
  branches = [],
  hasOtherOption = false,
  onAnswerChange,
  resetFlag
}) => {

  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [textResponse, setTextResponse] = useState("");
  const [otherText, setOtherText] = useState(""); 
  const [showBranches, setShowBranches] = useState([]);
  const [branchAnswers, setBranchAnswers] = useState([]);

  // Usar useCallback para memoizar la función y evitar recreaciones
  const isOtherOption = useCallback((optionId) => {
    const option = options.find(opt => opt && opt.id === optionId);
    return option && option.text && option.text.toLowerCase().includes("otros");
  }, [options]); // Dependencia: options

  // Usar useMemo para otherOption
  const otherOption = React.useMemo(() => 
    options.find(opt => 
      opt && opt.text && opt.text.toLowerCase().includes("otros")
    ), [options]);

  useEffect(() => {
    if (resetFlag) {
      setSelectedOption(null);
      setSelectedOptions([]);
      setTextResponse("");
      setOtherText(""); 
      setShowBranches([]);
      setBranchAnswers([]);
    }
  }, [resetFlag]);

  // Usar useRef para evitar logs duplicados
  const prevAnswerData = useRef(null);

  useEffect(() => {
    if (onAnswerChange) {
      let answerData = null;

      if (formatId === 1 && textResponse.trim() !== "") {
        answerData = {
          questionId,
          textResponse: textResponse.trim()
        };
      } else if (formatId === 2 && selectedOption) {
        answerData = {
          questionId,
          optionId: selectedOption
        };

        if (hasOtherOption && isOtherOption(selectedOption) && otherText.trim() !== "") {
          answerData.otherText = otherText.trim();
        }

      } else if (formatId === 3 && selectedOptions.length > 0) {
        answerData = {
          questionId,
          optionIds: [...selectedOptions]
        };

        if (hasOtherOption && selectedOptions.some(isOtherOption) && otherText.trim() !== "") {
          answerData.otherText = otherText.trim();
        }
      }

      // Solo loggear si los datos cambiaron
      if (answerData && JSON.stringify(answerData) !== JSON.stringify(prevAnswerData.current)) {
        prevAnswerData.current = answerData;
      }

      if (answerData && branchAnswers.length > 0) {
        answerData.branches = branchAnswers;
      }

      onAnswerChange(questionId, answerData);
    }
  }, [
    selectedOption, 
    selectedOptions, 
    textResponse, 
    otherText, 
    branchAnswers, 
    questionId, 
    formatId, 
    onAnswerChange, 
    hasOtherOption, 
    isOtherOption // Ahora está memoizado
  ]);

  const handleOptionSelect = (optionId) => {
    
    if (formatId === 2) {
      setSelectedOption(optionId);

      if (hasOtherOption && isOtherOption(optionId)) {
        setTimeout(() => {
          const otherInput = document.getElementById(`other-input-${questionId}`);
          if (otherInput) otherInput.focus();
        }, 100);
      }

      const branchesToShow = branches.filter(branch => 
        branch.triggerOptionId === optionId
      );
      
      if (branchesToShow.length > 0) {
        setShowBranches(branchesToShow); 
      } else {
        setShowBranches([]);
        setBranchAnswers([]);
      }
    } else if (formatId === 3) {
      if (selectedOptions.includes(optionId)) {
        setSelectedOptions(selectedOptions.filter(id => id !== optionId));

        if (hasOtherOption && isOtherOption(optionId)) {
          setOtherText("");
        }
      } else {
        setSelectedOptions([...selectedOptions, optionId]);
      }
    }
  };

  const handleTextChange = (e) => {
    setTextResponse(e.target.value);
  };

  const handleOtherTextChange = (e) => {
    setOtherText(e.target.value);
  };

  const handleBranchAnswerChange = useCallback((branchQuestionId, answerData) => {
    setBranchAnswers(prev => {
      const filtered = prev.filter(answer => answer.questionId !== branchQuestionId);
      if (answerData) {
        return [...filtered, answerData];
      }
      return filtered;
    });
  }, []);

  return (
    <>
    <div className="flex w-full md:w-[728px] min-h-[115px] rounded-lg shadow-[4px_4px_4px_0px_#C4C4C466]">
        <div className="w-4 rounded-tl-lg rounded-bl-lg bg-blue"></div>
        <div className="flex flex-col justify-between w-full gap-4 p-6 pb-8 bg-white rounded-tr-lg rounded-br-lg">
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <p className="font-itcdemi text-base text-[#52526C]">
                    {title}
                  </p>
                  {obligatory && (
                    <span className="text-red-500">*</span>
                  )}
                </div>
                {image && (
                <img src={image} className="w-full"/>
                )}

                {formatId === 1 && (
                  <input 
                    value={textResponse}
                    onChange={handleTextChange}
                    placeholder="Escribe tu respuesta aquí..."
                    className="border-b-[1px] my-4 outline-none text-base font-itcbook border-[#6B7280] w-full"
                  />
                )}

            {(formatId === 2 || formatId === 3) && (
              <div className="flex flex-col gap-3">
                {options.map((option) => (
                  <div key={option.id}>
                    <div 
                      onClick={() => handleOptionSelect(option.id)}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <div 
                        className={`flex items-center justify-center w-3 h-3 ${
                          formatId === 2 
                            ? 'rounded-full' 
                            : ''
                        } border-[#D9D9D9] border-[2px] cursor-pointer ${
                          (formatId === 2 && selectedOption === option.id) ||
                          (formatId === 3 && selectedOptions.includes(option.id))
                            ? 'bg-blue border-blue' 
                            : 'bg-white'
                        }`}  
                      >
                        <span className={`w-2 h-2 bg-blue ${
                          formatId === 2 ? 'rounded-full' : ''
                        } ${
                          (formatId === 2 && selectedOption === option.id) ||
                          (formatId === 3 && selectedOptions.includes(option.id))
                            ? 'opacity-100' 
                            : 'opacity-0'
                        } transition-opacity`}></span>
                      </div>
                      <span className="font-itcbook text-sm text-[#52526C]">
                        {option.text}
                      </span>
                    </div>

                    {/* Mostrar input para "Otros" cuando está seleccionado */}
                    {hasOtherOption && 
                     otherOption && 
                     option.id === otherOption.id && 
                     ((formatId === 2 && selectedOption === option.id) ||
                      (formatId === 3 && selectedOptions.includes(option.id))) && (
                      <div className="mt-2 ml-6">
                        <input
                          id={`other-input-${questionId}`}
                          type="text"
                          value={otherText}
                          onChange={handleOtherTextChange}
                          placeholder="Escribe tu opción..."
                          className="w-full p-1 text-sm border-b border-gray-300 placeholder:font-itcbook font-itcbook focus:outline-none "
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            </div>
        </div>
    </div>

      {/* Renderizar preguntas ramificadas */}
      {showBranches.map((branch) => (
        <div key={branch.id} className="ml-8">
          <AnswerQuestionSurvey 
            questionId={branch.id}
            formatId={branch.formatId}
            title={branch.text}
            image={branch.image}
            obligatory={branch.obligatory}
            options={branch.options || []} 
            hasOtherOption={branch.hasOtherOption}
            branches={branch.branches}
            onAnswerChange={handleBranchAnswerChange}
            resetFlag={resetFlag}
          />
        </div>
      ))}
      </>
  );
};

export default AnswerQuestionSurvey;